import { Utils } from '@common';
import { React } from '@common/3rd';
import { Filter, Modeller } from '@model';
import {
	Filterable,
	ForceListener,
	ModelDataChangedEvent,
	ModelWidgetInterface,
	ModelWidgetProps,
	VisibleFilterable,
	InitProcessor,
	InitProcessEvent
} from '@my-types';
import Widget from './widget';

/**
 * 从数据模型读取数据的组件
 */
abstract class ModelWidget<P extends ModelWidgetProps, S, C> extends Widget<P, S, C> implements ModelWidgetInterface {
	componentDidMount() {
		super.componentDidMount();
		this.installModelListeners();
	}

	/**
	 * 在数组模型下的组件, 重画会导致model重新被代理, 因此需要重加监听器
	 */
	componentDidUpdate(prevProps: any, prevState?: any, prevContext?: any) {
		super.componentDidUpdate(prevProps, prevState, prevContext);
		this.installModelListeners();
	}

	componentWillUnmount() {
		super.componentWillUnmount();
		this.uninstallModelListeners();
	}

	/**
	 * 测试是否可以监听指定的模型的指定属性
	 * @param propName 不传递此参数, 则默认取组件的属性名称
	 */
	protected isModelWatchable(model: any, propName?: string): boolean {
		return Modeller.isProxied(model) && !!(propName ? propName : this.getPropName());
	}

	protected installModelListeners(): this {
		if (this.isModelWatchable(this.getModel())) {
			this.getModel().$$on(this.getPropName(), this.dataChanged);
		}
		this.installVisibleListeners();

		return this;
	}

	protected uninstallModelListeners(): this {
		if (this.isModelWatchable(this.getModel())) {
			this.getModel().$$off(this.getPropName(), this.dataChanged);
		}
		this.uninstallVisibleListeners();
		return this;
	}

	/**
	 * 属性名称. 如果没有设置过, 返回空字符串
	 */
	getPropName(): string {
		return this.props.propName ? (this.props.propName as string) : this.props.id ? (this.props.id as string) : '';
	}

	/**
	 * 模型
	 */
	getModel(): any {
		return this.props.model || {};
	}

	/**
	 * 根模型
	 */
	getRootModel(): any {
		return this.props.root || this.getModel();
	}

	getArrayHolder(): any {
		return this.props.arrayHolder;
	}

	/**
	 * 从模型获取值, 属性名不提供则使用默认的属性
	 */
	protected getValueFromModel(propName?: string): any {
		return Modeller.getValue({ model: this.getModel(), root: this.getRootModel() }, propName || this.getPropName());
	}

	/**
	 * 设置值到DOM模型
	 */
	protected setValueToDOM(value: any, propName?: string): this {
		this.forceUpdate();
		return this;
	}

	/**
	 * 设置值到模型, 属性不提供则使用默认的属性
	 */
	protected setValueToModel(value: any, propName?: string): this {
		const prop = propName || this.getPropName();
		let oldValue = this.getValueFromModel(prop);
		if (oldValue === value) {
			// 值没有变
			if (Utils.isArray(oldValue) || Utils.isArray(value)) {
				// 如果新值或者旧值是数组, 强制刷dom
				return this.setValueToDOM(value, prop);
			}
			return this;
		}
		Modeller.setValue({ model: this.getModel(), root: this.getRootModel() }, prop, value);
		return this.setValueToDOM(value, prop);
	}

	protected dataChanged = (event: ModelDataChangedEvent): this => {
		if (event.prop === this.getPropName()) {
			this.setValueToDOM(event.newValue, event.prop);
			this.triggerForceListenEvent(event.oldValue);
		}
		return this;
	};

	protected isVisible(): boolean {
		const visible = this.getVisibleTrigger();
		if (Utils.isBoolean(visible)) {
			return visible;
		} else if (visible.length === 0) {
			return true;
		} else {
			const matchedFilter = this.matchFilter(visible) as VisibleFilterable;
			if (matchedFilter == null) {
				// 没有任何过滤器被匹配到, 不显示
				return false;
			} else {
				// 没有定义动作或者定义为true值
				return matchedFilter.do || Utils.isUndefined(matchedFilter.do);
			}
		}
	}

	/**
	 * 获取校验开启前置条件, 无论如何返回数组
	 */
	protected getVisibleTrigger(): VisibleFilterable[] | boolean {
		const visible = this.props.visible;
		if (Utils.isBoolean(visible)) {
			return visible;
		} else {
			return Utils.toArray(visible as any).map((filter: string | VisibleFilterable) => {
				if (Utils.isString(filter)) {
					return { on: filter, do: true } as VisibleFilterable;
				} else {
					return filter;
				}
			});
		}
	}

	protected forceUpdateForVisible = () => {
		this.forceUpdate();
	};

	protected installVisibleListeners(): this {
		if (this.getModel().$$proxied) {
			return this.careForVisibleListeners(item =>
				Modeller.asProxied(item.model).$$on(item.propName, this.forceUpdateForVisible)
			);
		} else {
			return this;
		}
	}

	protected uninstallVisibleListeners(): this {
		if (this.getModel().$$proxied) {
			return this.careForVisibleListeners(item =>
				Modeller.asProxied(item.model).$$off(item.propName, this.forceUpdateForVisible)
			);
		} else {
			return this;
		}
	}

	private careForVisibleListeners(handler: (item: { propName: string; model: any }) => void): this {
		const visible = this.getVisibleTrigger();
		if (!Utils.isBoolean(visible)) {
			Filter.getConcernedIdsOfFilters({
				filters: visible,
				root: this.getRootModel(),
				model: this.getModel(),
				caller: this
			}).forEach(handler);
		}
		return this;
	}

	/**
	 * 找到适配的过滤器
	 */
	protected matchFilter(filters?: Filterable[] | Filterable | null): Filterable | undefined {
		if (filters == null) {
			return undefined;
		}

		return Filter.findMatchedFilter({
			filters: Utils.toArray(filters),
			root: this.getRootModel(),
			model: this.getModel(),
			arrayHolder: this.getArrayHolder(),
			caller: this
		});
	}

	protected getForceListener(): ForceListener | undefined {
		return this.props.listen;
	}

	protected triggerForceListenEvent(oldValue: any): void {
		const listener = this.getForceListener();
		if (listener) {
			listener.call(this, {
				invoker: this,
				model: this.getModel(),
				root: this.getRootModel(),
				arrayHolder: this.getArrayHolder(),
				value: this.getValueFromModel(),
				oldValue
			});
		}
	}
	protected getInitProcessor(): InitProcessor | undefined {
		return this.props.process;
	}

	protected triggerInitProcessEvent(): void {
		const listener = this.getInitProcessor();
		if (listener) {
			listener.call(this, {
				invoker: this
			} as InitProcessEvent);
		}
	}
}

/**
 * 从数据模型读取数据的容器组件
 */
abstract class ModelContainerWidget<P extends ModelWidgetProps, S, C> extends ModelWidget<P, S, C> {
	protected renderChild(child: any, additionalProps?: any): any {
		if (child == null) {
			return null;
		}
		if (child.type && !Utils.isString(child.type)) {
			// 如果自己指定过, 都使用自己的
			// 如果没有指定过, 从当前组件往下传递
			const childProps = child.props;
			const overwrite = childProps.overwriteModel === true;
			return React.cloneElement(
				child,
				Object.assign(
					{
						// 传递数据模型
						model: overwrite ? childProps.model || this.getModel() : this.getModel(),
						root: overwrite ? childProps.root || this.getRootModel() : this.getRootModel(),
						arrayHolder: overwrite
							? childProps.arrayHolder || this.getArrayHolder()
							: this.getArrayHolder(),
						parentContainer: this
					},
					additionalProps
				),
				childProps.children
			);
		} else {
			return child;
		}
	}

	protected renderChildren(): any {
		return React.Children.map(this.props.children, (child: any) => {
			return this.renderChild(child);
		});
	}
}

export { ModelWidget, ModelContainerWidget };
